<template>
    <Teleport to="#summary-banner-target">
        <section v-if="isVisible" class="stf-summary-banner">
            <StfContainer>
                <div class="stf-summary-banner-wrapper">
                    <div class="stf-summary-banner-content">
                        <div class="stf-summary-left">
                            <StfIcon class="stf-summary-icon" icon="cart" size="small" color="black" />
                            <span class="stf-summary-pill">{{ totalClasses }} {{ totalClasses === 1 ? 'Class' : 'Classes' }}</span>
                            <span class="stf-summary-pill-white">{{ totalGoods }} {{ totalGoods === 1 ? 'Item' : 'Items' }}</span>
                        </div>
                        <div class="stf-summary-right" v-if="country && totalPrice > 0 && totalClasses > 0">
                            <div class="stf-summary-price-wrapper">
                                <div v-tooltip.bottomRight="tooltipHtml"> 
                                    <StfIcon class="stf-summary-info-icon" icon="info" size="small" color="black" />
                                </div>
                                <StfSubHeading >{{ formatAmountWithCurrency(totalPrice, country.currency) }}</StfSubHeading>
                            </div>
                        </div>
                    </div>
                </div>
            </StfContainer>
        </section>
    </Teleport>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useProductStore } from '@/store/good';
import { useCartStore } from '@/store/cart';
import { storeToRefs } from 'pinia';
import StfIcon from '@/ui/icon/StfIcon.vue';
import StfContainer from '@/ui/container/StfContainer.vue';
import { formatAmountWithCurrency } from '@/filter/money-filter';
import useCountry from '@/composables/countryDependable';
import { useI18n } from 'vue-i18n';
import { buildtooltip } from '@/components/goods-view/components/cartPriceTooltipContent';
import StfSubHeading from '@/ui/typography/StfSubHeading.vue';
import useCurrentSearch from '@/components/goods-view/searchDataStore';

const productStore = useProductStore();
const { totalGoods, totalClasses } = storeToRefs(productStore);
const { t } = useI18n();

// We can use useCountry() since we're teleported from router context
const country = useCountry();
const cartStore = useCartStore();
const { totalPrice, cart } = storeToRefs(cartStore);

// Get current search data
const { haveSearched } = useCurrentSearch();

// Track if banner has been shown once
const hasBeenShown = ref(false);

const tooltipHtml = computed(() => {
    if (cart.value === null || !country) {
        return '';
    }
    return buildtooltip(t, country, cart.value);
});

const shouldShow = computed(() => {
    return haveSearched.value || totalGoods.value > 0 || totalClasses.value > 0;
});

// Watch for when banner should be shown and mark it as shown
watch(shouldShow, (newValue) => {
    if (newValue && !hasBeenShown.value) {
        hasBeenShown.value = true;
    }
}, { immediate: true });

const isVisible = computed(() => {
    return shouldShow.value || hasBeenShown.value;
});
</script>

<style scoped lang="scss">
@use '@/assets/sass/_variables' as vars;

.stf-summary-banner {
    background-color: vars.$color-brand-light;
    width: 100%;
}

.stf-summary-banner-wrapper {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 15px 36px;
    position: relative;
    
    @media (max-width: 768px) {
        padding: 15px 10px 15px 0px;
    }
}

.stf-summary-banner-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
}

.stf-summary-left {
    display: flex;
    align-items: center;
}

.stf-summary-right {
    display: flex;
    align-items: center;
}

.stf-summary-icon {
    margin-right: 10px;
}

.stf-summary-pill {
    background-color: vars.$color-brand;
    color: vars.$color-white;
    padding: 6px 12px;
    border-radius: 20px;
    margin-right: -22px;
    position: relative;
    z-index: 2;
}

.stf-summary-pill-white {
    background-color: vars.$color-white;
    color: vars.$color-gray-600;
    padding: 6px 12px 6px 27px;
    border-radius: 20px;
    position: relative;
    z-index: 1;
}

.stf-summary-price-wrapper {
    display: flex;
    align-items: center;
    gap: 8px;
}

/* Mobile styles */
@media (max-width: 768px) {

    .stf-summary-pill {
        background-color: vars.$color-brand;
        padding: 4px 12px;
    }

    .stf-summary-pill-white {
        padding: 4px 12px 4px 27px;
    }
}
</style>